import React from 'react';
import Util from '../../../../forecast-app/shared/util/util';
import {HighLevelNumbersBox, HighLevelNumbersContainer} from './HighLevelNumbers.styled';
import {getCurrency, getFixedPriceExpenseTotals, NUMBER_TYPE} from './util/BudgetUtils';
import {BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../constants';
import {createFragmentContainer, graphql} from 'react-relay';
import {HighLevelNumber} from './HighLevelNumber';
import {hasFeatureFlag} from '../../../../forecast-app/shared/util/FeatureUtil';
import ProjectUtil from '../../../../forecast-app/shared/util/ProjectUtil';
import {useIntl} from 'react-intl';
import CompanySetupUtil from '../../../../forecast-app/shared/util/CompanySetupUtil';

const HighLevelBudgetNumbers = ({project, company}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const projectTracksRevenueRecognition = ProjectUtil.projectHasRevenueRecognition();

	// Just check cost access. At this point revenue access is implicit.
	const hasCostAccess = !Util.hasRevenueWithoutCostAccess();

	const {
		useBaseline,
		hasInvoices,
		budget,
		budgetType,
		defaultPeriodBudgetType,
		financialNumbers,
		financialNumbersToCurrentMonth,
	} = project;

	const {
		billableActualTimeAndExpenses,
		actualCost,
		forecastCostToComplete,
		totalCostAtCompletion,
		billableForecastTimeAndExpensesToComplete,
		billableTotalTimeAndExpensesAtCompletion,
		recognitionProfit,
		recognitionProfitPercentage,
		totalRevenueRecognition,
		projectTotalForecastTimeAndExpensesToCompleteInPast,
		projectTotalForecastCostToCompleteInPast,
	} = financialNumbers;

	const {
		recognitionLockedProfit: recognitionLockedProfitToCurrentMonth,
		recognitionLockedProfitPercentage: recognitionLockedProfitPercentageToCurrentMonth,
		recognitionLockedRevenue: recognitionLockedRevenueToCurrentMonth,
	} = financialNumbersToCurrentMonth;

	const expenseBillableTotalTimeAndExpensesAtCompletion = getFixedPriceExpenseTotals(project.expenseItems?.edges);

	const currency = getCurrency(company, project);
	const currencySymbol = Util.GetCurrencySymbol(currency);
	const projectTracksRevenue = ProjectUtil.projectTracksRevenue(project);
	const isRetainerFixedPrice =
		budgetType === BUDGET_TYPE.RETAINER && defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const isFixedPrice =
		projectTracksRevenue &&
		(budgetType === BUDGET_TYPE.FIXED_PRICE || budgetType === BUDGET_TYPE.FIXED_PRICE_V2 || isRetainerFixedPrice);
	const isFixedPriceV2 =
		projectTracksRevenue &&
		(budgetType === BUDGET_TYPE.FIXED_PRICE_V2 || isRetainerFixedPrice || budgetType === BUDGET_TYPE.FIXED_PRICE);
	const totalBudget = budget + expenseBillableTotalTimeAndExpensesAtCompletion;
	const hasExpenseRevenue = expenseBillableTotalTimeAndExpensesAtCompletion > 0;
	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	const showSimplifiedSummaryTable =
		hasFinancialCategoriesUpdate &&
		projectTracksRevenue &&
		(budgetType === BUDGET_TYPE.TIME_AND_MATERIALS ||
			(budgetType === BUDGET_TYPE.RETAINER && defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS) ||
			(budgetType === BUDGET_TYPE.RETAINER && defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS));

	const ExpenseRelatedNumbers = () =>
		!!hasExpenseRevenue && (
			<>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'project_budget.revenue_from_expenses'}
					value={expenseBillableTotalTimeAndExpensesAtCompletion}
					numberType={NUMBER_TYPE.MONEY}
					cy={'revenue-from-expenses'}
				/>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'common.projected_total_revenue'}
					value={totalBudget}
					numberType={NUMBER_TYPE.MONEY}
					cy={'revenue-from-expenses'}
				/>
			</>
		);

	const RevenueRelatedNumbers = () =>
		hasCostAccess && (
			<>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'common.projected_total_cost'}
					value={totalCostAtCompletion}
					numberType={NUMBER_TYPE.MONEY}
					cy={'projected-total-cost'}
				/>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'common.projected_total_profit'}
					value={recognitionProfit}
					numberType={NUMBER_TYPE.MONEY}
					cy={'projected-total-profit'}
				/>
				<HighLevelNumber
					labelTranslationId={'common.projected_total_margin'}
					value={recognitionProfitPercentage}
					numberType={NUMBER_TYPE.PERCENTAGE}
					cy={'projected-total-margin'}
				/>
			</>
		);

	return (
		<HighLevelNumbersContainer data-cy={'summary-table'}>
			{showSimplifiedSummaryTable ? (
				<SimplifiedSummaryTable
					useBaseline={useBaseline}
					hasInvoices={hasInvoices}
					currencySymbol={currencySymbol}
					financialNumbers={financialNumbers}
				/>
			) : hasFinancialCategoriesUpdate && isFixedPriceV2 ? (
				isRetainerFixedPrice ? (
					<HighLevelNumbersBox wide>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'retainer.total_price_of_periods'}
							value={budget}
							numberType={NUMBER_TYPE.MONEY}
							cy={'fixed-price-budget'}
						/>
						<ExpenseRelatedNumbers />
						<RevenueRelatedNumbers />
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'retainer.average_actual_hours'}
							value={'-' /* TODO: Depends on T53000 */}
							numberType={undefined}
							cy={'fixed-price-budget'}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'retainer.average_profit'}
							value={'-' /* TODO: Depends on T53000 */}
							numberType={undefined}
							cy={'fixed-price-budget'}
						/>
					</HighLevelNumbersBox>
				) : (
					<HighLevelNumbersBox wide>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.fixed_price'}
							value={budget}
							numberType={NUMBER_TYPE.MONEY}
							cy={'fixed-price-budget'}
						/>
						<ExpenseRelatedNumbers />
						<RevenueRelatedNumbers />
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.projected_total_billable_value_of_service'}
							value={billableTotalTimeAndExpensesAtCompletion}
							numberType={NUMBER_TYPE.MONEY}
							cy={'projected-total-billable-value-of-service'}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.projected_remaining_budget'}
							value={totalBudget - billableTotalTimeAndExpensesAtCompletion}
							numberType={NUMBER_TYPE.MONEY}
							cy={'projected-remaining-budget'}
							tooltipItems={[
								{
									title: formatMessage({id: 'common.projected_remaining_budget'}),
									description: formatMessage({
										id: 'project_budget.projected_remaining_budget.tooltip',
									}),
									details: [
										formatMessage({
											id: hasExpenseRevenue
												? 'common.projected_total_revenue'
												: 'project_budget.fixed_price',
										}),
										'-',
										formatMessage({id: 'common.projected_total_billable_value_of_service'}),
									],
								},
							]}
						/>
					</HighLevelNumbersBox>
				)
			) : isFixedPriceV2 ? (
				<>
					<HighLevelNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.project_fixed_price'}
							value={budget}
							numberType={NUMBER_TYPE.MONEY}
							cy={'fixed-price-budget'}
							tooltipItems={[{description: formatMessage({id: 'project_budget.project_fixed_price_tooltip'})}]}
						/>
						{hasExpenseRevenue && (
							<>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.revenue_from_expenses'}
									value={expenseBillableTotalTimeAndExpensesAtCompletion}
									numberType={NUMBER_TYPE.MONEY}
									cy={'billable-expenses'}
									tooltipItems={[
										{description: formatMessage({id: 'project_budget.revenue_from_expenses_tooltip'})},
									]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.total_revenue'}
									value={totalBudget}
									numberType={NUMBER_TYPE.MONEY}
									cy={'total-budget'}
									tooltipItems={[
										{
											title: formatMessage({id: 'project_budget.total_revenue'}),
											details: [
												formatMessage({id: 'project_budget.project_fixed_price'}),
												'+',
												formatMessage({id: 'project_budget.revenue_from_expenses'}),
											],
										},
									]}
								/>
							</>
						)}
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.actual_billable_spend_to_date'}
							value={billableActualTimeAndExpenses}
							numberType={NUMBER_TYPE.MONEY}
							cy={'actual-spend-to-date'}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.budget_remaining'}
							value={totalBudget - billableActualTimeAndExpenses}
							numberType={NUMBER_TYPE.MONEY}
							cy={'budget-remaining'}
							bold
						/>
					</HighLevelNumbersBox>
					<HighLevelNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.high_level_numbers.forecast_to_complete'}
							value={billableForecastTimeAndExpensesToComplete}
							numberType={NUMBER_TYPE.MONEY}
							cy={'remaining-revenue'}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.high_level_numbers.total_at_completion'}
							value={billableTotalTimeAndExpensesAtCompletion}
							numberType={NUMBER_TYPE.MONEY}
							cy={'forecast-revenue'}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={
								hasExpenseRevenue
									? 'project_budget.variance_forecast_total_revenue'
									: 'project_budget.variance_forecast_budget'
							}
							value={totalBudget - billableTotalTimeAndExpensesAtCompletion}
							numberType={NUMBER_TYPE.MONEY}
							cy={'variance-forecast-budget'}
							bold
						/>
					</HighLevelNumbersBox>

					{hasCostAccess && (
						<HighLevelNumbersBox>
							<HighLevelNumber
								currencySymbol={currencySymbol}
								labelTranslationId={'project_budget.actual_cost_to_date'}
								value={actualCost}
								numberType={NUMBER_TYPE.MONEY}
								cy={'actual-cost-to-date'}
							/>
							<HighLevelNumber
								currencySymbol={currencySymbol}
								labelTranslationId={'project_budget.high_level_numbers.forecast_cost_to_complete'}
								value={forecastCostToComplete}
								numberType={NUMBER_TYPE.MONEY}
								cy={'remaining-cost'}
							/>
							<HighLevelNumber
								currencySymbol={currencySymbol}
								labelTranslationId={'project_budget.total_cost_at_completion'}
								value={totalCostAtCompletion}
								numberType={NUMBER_TYPE.MONEY}
								cy={'forecast-cost'}
							/>
							<HighLevelNumber
								currencySymbol={currencySymbol}
								labelTranslationId={'project_budget.high_level_numbers.forecast_profit_at_completion'}
								value={recognitionProfit}
								numberType={NUMBER_TYPE.MONEY}
								cy={'forecast-profit'}
							/>
							<HighLevelNumber
								currencySymbol={currencySymbol}
								labelTranslationId={'project_budget.high_level_numbers.forecast_profit_at_completion_percent'}
								value={recognitionProfitPercentage}
								numberType={NUMBER_TYPE.PERCENTAGE}
								cy={'forecast-profit-percentage'}
							/>
						</HighLevelNumbersBox>
					)}
					<HighLevelNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.forecast_time_in_the_past'}
							value={projectTotalForecastTimeAndExpensesToCompleteInPast}
							numberType={NUMBER_TYPE.MONEY}
							cy={'budget-forecast-time-in-past'}
							red={projectTotalForecastTimeAndExpensesToCompleteInPast > 0}
						/>
					</HighLevelNumbersBox>
				</>
			) : (
				<>
					{isFixedPrice ? (
						<>
							<HighLevelNumbersBox>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.fixed_price_revenue_brackets'}
									value={budget}
									numberType={NUMBER_TYPE.MONEY}
									cy={'fixed-price-revenue'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.actual_revenue'}
									value={billableActualTimeAndExpenses}
									numberType={NUMBER_TYPE.MONEY}
									cy={'actual-revenue'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.remaining_revenue'}
									value={billableForecastTimeAndExpensesToComplete}
									numberType={NUMBER_TYPE.MONEY}
									cy={'remaining-revenue'}
								/>
								{hasCostAccess && (
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'common.cost'}
										value={totalCostAtCompletion}
										numberType={NUMBER_TYPE.MONEY}
										cy={'cost'}
									/>
								)}
								{hasCostAccess && (
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'common.profit'}
										value={budget - totalCostAtCompletion}
										numberType={NUMBER_TYPE.MONEY}
										cy={'profit'}
									/>
								)}
							</HighLevelNumbersBox>
							<HighLevelNumbersBox>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.forecast_time_in_the_past'}
									value={projectTotalForecastTimeAndExpensesToCompleteInPast}
									numberType={NUMBER_TYPE.MONEY}
									cy={'budget-forecast-time-in-past'}
									red={projectTotalForecastTimeAndExpensesToCompleteInPast > 0}
								/>
							</HighLevelNumbersBox>
						</>
					) : !ProjectUtil.projectTracksRevenue(project) ? (
						hasFinancialCategoriesUpdate ? (
							<NonBillableProjectSummaryTable
								useBaseline={useBaseline}
								currencySymbol={currencySymbol}
								financialNumbers={financialNumbers}
								hasCostAccess={hasCostAccess}
							/>
						) : (
							<NonBillableProjectSummaryTableWithoutFinancialCategoriesUpdate
								hasCostAccess={hasCostAccess}
								currencySymbol={currencySymbol}
								actualCost={actualCost}
								forecastCostToComplete={forecastCostToComplete}
								totalCostAtCompletion={totalCostAtCompletion}
								projectTotalForecastCostToCompleteInPast={projectTotalForecastCostToCompleteInPast}
							/>
						)
					) : (
						<>
							<HighLevelNumbersBox>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.actual_billable_spend_to_date'}
									value={billableActualTimeAndExpenses}
									numberType={NUMBER_TYPE.MONEY}
									cy={'actual-spend-to-date'}
									bold
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.high_level_numbers.forecast_to_complete'}
									value={billableForecastTimeAndExpensesToComplete}
									numberType={NUMBER_TYPE.MONEY}
									cy={'remaining-revenue'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.high_level_numbers.total_at_completion'}
									value={billableTotalTimeAndExpensesAtCompletion}
									numberType={NUMBER_TYPE.MONEY}
									cy={'forecast-revenue'}
								/>
							</HighLevelNumbersBox>
							{hasCostAccess && (
								<HighLevelNumbersBox>
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'project_budget.actual_cost_to_date'}
										value={actualCost}
										numberType={NUMBER_TYPE.MONEY}
										cy={'actual-cost-to-date'}
										bold
									/>
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'project_budget.high_level_numbers.forecast_cost_to_complete'}
										value={forecastCostToComplete}
										numberType={NUMBER_TYPE.MONEY}
										cy={'remaining-cost'}
									/>
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'project_budget.total_cost_at_completion'}
										value={totalCostAtCompletion}
										numberType={NUMBER_TYPE.MONEY}
										cy={'forecast-cost'}
									/>
								</HighLevelNumbersBox>
							)}
							{CompanySetupUtil.hasFinance() && projectTracksRevenueRecognition && (
								<>
									<HighLevelNumbersBox>
										<HighLevelNumber
											currencySymbol={currencySymbol}
											labelTranslationId={
												'project_budget.high_level_numbers.revenue_recognition_locked_to_current_month'
											}
											value={recognitionLockedRevenueToCurrentMonth}
											numberType={NUMBER_TYPE.MONEY}
											cy={'forecast-profit'}
											bold
										/>
										<HighLevelNumber
											currencySymbol={currencySymbol}
											labelTranslationId={'project_budget.high_level_numbers.forecast_profit_to_date'}
											value={recognitionLockedProfitToCurrentMonth}
											numberType={NUMBER_TYPE.MONEY}
											cy={'forecast-profit'}
										/>
										<HighLevelNumber
											currencySymbol={currencySymbol}
											labelTranslationId={
												'project_budget.high_level_numbers.forecast_profit_to_date_percent'
											}
											value={recognitionLockedProfitPercentageToCurrentMonth}
											numberType={NUMBER_TYPE.PERCENTAGE}
											cy={'forecast-profit-percentage'}
										/>
									</HighLevelNumbersBox>
									<HighLevelNumbersBox>
										<HighLevelNumber
											currencySymbol={currencySymbol}
											labelTranslationId={'project_budget.recognition_amount'}
											value={totalRevenueRecognition}
											numberType={NUMBER_TYPE.MONEY}
											cy={'forecast-profit'}
											bold
										/>
										<HighLevelNumber
											currencySymbol={currencySymbol}
											labelTranslationId={
												'project_budget.high_level_numbers.forecast_profit_at_completion'
											}
											value={recognitionProfit}
											numberType={NUMBER_TYPE.MONEY}
											cy={'forecast-profit'}
										/>
										<HighLevelNumber
											currencySymbol={currencySymbol}
											labelTranslationId={
												'project_budget.high_level_numbers.forecast_profit_at_completion_percent'
											}
											value={recognitionProfitPercentage}
											numberType={NUMBER_TYPE.PERCENTAGE}
											cy={'forecast-profit-percentage'}
										/>
									</HighLevelNumbersBox>
								</>
							)}
							<HighLevelNumbersBox>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.forecast_time_in_the_past'}
									value={projectTotalForecastTimeAndExpensesToCompleteInPast}
									numberType={NUMBER_TYPE.MONEY}
									cy={'budget-forecast-time-in-past'}
									red={projectTotalForecastTimeAndExpensesToCompleteInPast > 0}
								/>
							</HighLevelNumbersBox>
						</>
					)}
				</>
			)}
		</HighLevelNumbersContainer>
	);
};

const SimplifiedSummaryTable = ({useBaseline, hasInvoices, currencySymbol, financialNumbers}) => {
	const {
		baselineRevenue,
		actualRevenue,
		totalRevenueRecognition,
		baselineCost,
		actualCost,
		totalCostAtCompletion,
		baselineProfit,
		actualProfit,
		totalRevenueProfitAtCompletion,
		baselineMargin,
		actualMargin,
		totalRevenueMarginAtCompletion,
		invoiced,
	} = financialNumbers;

	const hasCostAccess = !Util.hasRevenueWithoutCostAccess();

	return (
		<>
			{useBaseline ? (
				<>
					<HighLevelNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.baseline_revenue'}
							value={baselineRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'baseline-revenue'}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.actual_revenue'}
							value={actualRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'actual-revenue'}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.projected_total_revenue'}
							value={totalRevenueRecognition}
							numberType={NUMBER_TYPE.MONEY}
							cy={'projected-total-revenue'}
						/>
					</HighLevelNumbersBox>
					{hasCostAccess && (
						<>
							<HighLevelNumbersBox>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.baseline_cost'}
									value={baselineCost}
									numberType={NUMBER_TYPE.MONEY}
									cy={'baseline-cost'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.actual_cost'}
									value={actualCost}
									numberType={NUMBER_TYPE.MONEY}
									cy={'actual-cost'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.projected_total_cost'}
									value={totalCostAtCompletion}
									numberType={NUMBER_TYPE.MONEY}
									cy={'projected-total-cost'}
								/>
							</HighLevelNumbersBox>
							<HighLevelNumbersBox>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.baseline_profit'}
									value={baselineProfit}
									numberType={NUMBER_TYPE.MONEY}
									cy={'baseline-profit'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.actual_profit'}
									value={actualProfit}
									numberType={NUMBER_TYPE.MONEY}
									cy={'actual-profit'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.projected_total_profit'}
									value={totalRevenueProfitAtCompletion}
									numberType={NUMBER_TYPE.MONEY}
									cy={'projected-total-profit'}
								/>
							</HighLevelNumbersBox>
							<HighLevelNumbersBox>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.baseline_margin'}
									value={baselineMargin}
									numberType={NUMBER_TYPE.PERCENTAGE}
									cy={'baseline-margin'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.actual_margin'}
									value={actualMargin}
									numberType={NUMBER_TYPE.PERCENTAGE}
									cy={'actual-margin'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.projected_total_margin'}
									value={totalRevenueMarginAtCompletion}
									numberType={NUMBER_TYPE.PERCENTAGE}
									cy={'projected-total-margin'}
								/>
							</HighLevelNumbersBox>
						</>
					)}
				</>
			) : (
				<>
					<HighLevelNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.actual_revenue'}
							value={actualRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'actual-revenue'}
						/>
						{hasCostAccess && (
							<>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.actual_cost'}
									value={actualCost}
									numberType={NUMBER_TYPE.MONEY}
									cy={'actual-cost'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.actual_profit'}
									value={actualProfit}
									numberType={NUMBER_TYPE.MONEY}
									cy={'actual-profit'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.actual_margin'}
									value={actualMargin}
									numberType={NUMBER_TYPE.PERCENTAGE}
									cy={'actual-margin'}
								/>
							</>
						)}
					</HighLevelNumbersBox>
					<HighLevelNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.projected_total_revenue'}
							value={totalRevenueRecognition}
							numberType={NUMBER_TYPE.MONEY}
							cy={'projected-total-revenue'}
						/>
						{hasCostAccess && (
							<>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.projected_total_cost'}
									value={totalCostAtCompletion}
									numberType={NUMBER_TYPE.MONEY}
									cy={'projected-total-cost'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.projected_total_profit'}
									value={totalRevenueProfitAtCompletion}
									numberType={NUMBER_TYPE.MONEY}
									cy={'projected-total-profit'}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.projected_total_margin'}
									value={totalRevenueMarginAtCompletion}
									numberType={NUMBER_TYPE.PERCENTAGE}
									cy={'projected-total-margin'}
								/>
							</>
						)}
					</HighLevelNumbersBox>
				</>
			)}
			{hasInvoices && (
				<HighLevelNumbersBox>
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.invoiced'}
						value={invoiced}
						numberType={NUMBER_TYPE.MONEY}
						cy={'invoiced'}
					/>
				</HighLevelNumbersBox>
			)}
		</>
	);
};

const NonBillableProjectSummaryTableWithoutFinancialCategoriesUpdate = ({
	currencySymbol,
	actualCost,
	forecastCostToComplete,
	totalCostAtCompletion,
	projectTotalForecastCostToCompleteInPast,
}) => {
	const hasCostAccess = !Util.hasRevenueWithoutCostAccess();

	return (
		<>
			{hasCostAccess && (
				<HighLevelNumbersBox>
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'project_budget.actual_cost_to_date'}
						value={actualCost}
						numberType={NUMBER_TYPE.MONEY}
						cy={'actual-cost-to-date'}
					/>
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'project_budget.high_level_numbers.forecast_cost_to_complete'}
						value={forecastCostToComplete}
						numberType={NUMBER_TYPE.MONEY}
						cy={'remaining-cost'}
					/>
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'project_budget.total_cost_at_completion'}
						value={totalCostAtCompletion}
						numberType={NUMBER_TYPE.MONEY}
						cy={'forecast-cost'}
					/>
				</HighLevelNumbersBox>
			)}
			<HighLevelNumbersBox>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'project_budget.forecast_cost_in_the_past'}
					value={projectTotalForecastCostToCompleteInPast}
					numberType={NUMBER_TYPE.MONEY}
					cy={'budget-forecast-cost-in-past'}
					red={projectTotalForecastCostToCompleteInPast > 0}
				/>
			</HighLevelNumbersBox>
		</>
	);
};

const NonBillableProjectSummaryTable = ({useBaseline, currencySymbol, financialNumbers}) => {
	const {
		actualCost,
		forecastCostToComplete,
		totalCostAtCompletion,
		baselineCost,
		baselineVsForecastCost,
		allActualTimeAndExpenses,
		allForecastTimeAndExpensesToComplete,
		allTotalTimeAndExpensesAtCompletion,
		baselineTimeAndExpenses,
		baselineVsBillableTotalTimeAndExpensesAtCompletion,
		estimatedCost,
		planVsTotalCostAtCompletion,
		billablePlannedTimeAndExpenses,
		billableTotalTimeAndExpensesAtCompletion,
	} = financialNumbers;
	const hasCostAccess = !Util.hasRevenueWithoutCostAccess();

	return (
		<>
			{hasCostAccess && (
				<HighLevelNumbersBox>
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.actual_cost'}
						value={actualCost}
						numberType={NUMBER_TYPE.MONEY}
						cy={'actual-cost'}
					/>
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.remaining_work_cost'}
						value={forecastCostToComplete}
						numberType={NUMBER_TYPE.MONEY}
						cy={'remaining-work-cost'}
					/>
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.projected_total_cost'}
						value={totalCostAtCompletion}
						numberType={NUMBER_TYPE.MONEY}
						cy={'projected-total-cost'}
					/>
					{useBaseline ? (
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.baseline_cost'}
							value={baselineCost}
							numberType={NUMBER_TYPE.MONEY}
							cy={'baseline-cost'}
						/>
					) : (
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.estimated_cost'}
							value={estimatedCost}
							numberType={NUMBER_TYPE.MONEY}
							cy={'estimated-cost'}
						/>
					)}
					{useBaseline ? (
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.baseline_projected_total_cost'}
							value={baselineVsForecastCost}
							numberType={NUMBER_TYPE.MONEY}
							cy={'baseline-projected-total-cost'}
						/>
					) : (
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'common.estimated_projected_total_cost'}
							value={planVsTotalCostAtCompletion}
							numberType={NUMBER_TYPE.MONEY}
							cy={'estimated-projected-total-cost'}
						/>
					)}
				</HighLevelNumbersBox>
			)}
			<HighLevelNumbersBox>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'common.actual_value_of_service'}
					value={allActualTimeAndExpenses}
					numberType={NUMBER_TYPE.MONEY}
					cy={'actual-value-of-service'}
				/>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'common.remaining_work_value_of_service'}
					value={allForecastTimeAndExpensesToComplete}
					numberType={NUMBER_TYPE.MONEY}
					cy={'remaining-work-value-of-service'}
				/>
				<HighLevelNumber
					currencySymbol={currencySymbol}
					labelTranslationId={'common.projected_total_value_of_service'}
					value={allTotalTimeAndExpensesAtCompletion}
					numberType={NUMBER_TYPE.MONEY}
					cy={'projected-total-value-of-service'}
				/>
				{useBaseline ? (
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.baseline_value_of_service'}
						value={baselineTimeAndExpenses}
						numberType={NUMBER_TYPE.MONEY}
						cy={'baseline-value-of-service'}
					/>
				) : (
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.estimated_value_of_service'}
						value={billablePlannedTimeAndExpenses}
						numberType={NUMBER_TYPE.MONEY}
						cy={'estimated-value-of-service'}
					/>
				)}
				{useBaseline ? (
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.baseline_projected_total_value_of_service'}
						value={baselineVsBillableTotalTimeAndExpensesAtCompletion}
						numberType={NUMBER_TYPE.MONEY}
						cy={'baseline-projected-total-value-of-service'}
					/>
				) : (
					<HighLevelNumber
						currencySymbol={currencySymbol}
						labelTranslationId={'common.estimated_projected_total_value_of_service'}
						value={billableTotalTimeAndExpensesAtCompletion}
						numberType={NUMBER_TYPE.MONEY}
						cy={'estimated-projected-total-value-of-service'}
					/>
				)}
			</HighLevelNumbersBox>
		</>
	);
};

export default createFragmentContainer(HighLevelBudgetNumbers, {
	company: graphql`
		fragment HighLevelBudgetNumbers_company on Company {
			tier
			id
			currency
		}
	`,
	project: graphql`
		fragment HighLevelBudgetNumbers_project on ProjectType
		@argumentDefinitions(currentYear: {type: "Int"}, currentMonth: {type: "Int"}, currentMonthLastDay: {type: "Int"}) {
			id
			useBaseline
			hasInvoices
			budget
			budgetType
			defaultPeriodBudgetType
			timeRegBasedPlusDoneScopeCompletion
			rateCard {
				currency
			}
			financialNumbers(convertToProjectCurrency: true) {
				baselineRevenue
				actualRevenue
				baselineProfit
				actualProfit
				totalRevenueProfitAtCompletion
				baselineMargin
				actualMargin
				totalRevenueMarginAtCompletion
				billableActualTimeAndExpenses
				actualCost
				billableForecastTimeAndExpensesToComplete
				forecastCostToComplete
				totalCostAtCompletion
				billableTotalTimeAndExpensesAtCompletion
				invoiced
				paid
				recognitionProfit
				recognitionProfitPercentage
				totalRevenueRecognition
				projectTotalForecastTimeAndExpensesToCompleteInPast
				projectTotalForecastCostToCompleteInPast
				baselineCost
				baselineVsForecastCost
				allActualTimeAndExpenses
				allForecastTimeAndExpensesToComplete
				allTotalTimeAndExpensesAtCompletion
				baselineTimeAndExpenses
				baselineVsBillableTotalTimeAndExpensesAtCompletion
				estimatedCost
				planVsTotalCostAtCompletion
				billablePlannedTimeAndExpenses
				billableTotalTimeAndExpensesAtCompletion
			}
			financialNumbersToCurrentMonth: financialNumbers(
				endYear: $currentYear
				endMonth: $currentMonth
				endDay: $currentMonthLastDay
				convertToProjectCurrency: true
			) {
				recognitionLockedProfit
				recognitionLockedProfitPercentage
				recognitionLockedRevenue
			}
			expenseItems(first: 10000) @connection(key: "Project_expenseItems") {
				edges {
					node {
						approved
						billable
						partOfFixedPrice
						expenseYear
						expenseMonth
						expenseDay
						price
						cost
						quantity
					}
				}
			}
		}
	`,
});
